import { createSelector } from "reselect";

import { generalLedgerAccountsSelector } from "src/store/glAccounts/selector";
import {
  scheduleCGroupingsSelector as systemScheduleCGroupingsSelector,
  scheduleCMappingsSelector as systemScheduleCMappingsSelector,
} from "src/store/system/selector";
import type { ExpenseType } from "src/interfaces/systemState.interface";
import type { GeneralLedgerAccountV2Type } from "src/interfaces/generalLedgerAccount.interface";

export const scheduleCAccountsSelector = createSelector(
  [generalLedgerAccountsSelector],
  (accounts) => {
    const all = [
      {
        id: "sales-transaction-fees",
        name: "Sales - Transaction Fees",
        type: ["expenses", "cost-of-goods-sold"] as GeneralLedgerAccountV2Type,
      },
      {
        id: "sales-shipping-costs",
        name: "Sales - Shipping Costs",
        type: ["expenses", "cost-of-goods-sold"] as GeneralLedgerAccountV2Type,
      },
      ...accounts.filter(
        (account) =>
          account.type[0] !== "assets" &&
          account.type[0] !== "liabilities" &&
          account.type[0] !== "equity" &&
          account.type[0] !== "income"
      ),
    ];

    all.sort((a, b) => a.name.localeCompare(b.name));

    return all;
  }
);

export const scheduleCGroupingsSelector = createSelector(
  [generalLedgerAccountsSelector, systemScheduleCGroupingsSelector],
  (accounts, grouppings) => {
    const groups: Record<
      string,
      {
        id: string;
        name: string;
        group: string;
      }
    > = {};
    for (const [key, value] of Object.entries(grouppings)) {
      let account = accounts.find((a) => a.id === key);
      if (!account) account = accounts.find((a) => a.name === key);

      if (account) {
        if (
          account.type[0] !== "assets" &&
          account.type[0] !== "liabilities" &&
          account.type[0] !== "equity" &&
          account.type[0] !== "income"
        ) {
          groups[account.id] = {
            id: account.id,
            name: account.name,
            group: value,
          };
        }
      }
    }

    return groups;
  }
);

export const scheduleCMappingsSelector = createSelector(
  [scheduleCAccountsSelector, systemScheduleCMappingsSelector],
  (accounts, mappings) => {
    const map: Record<string, ExpenseType> = {};
    for (const [key, value] of Object.entries(mappings)) {
      let account = accounts.find((a) => a.id === key);
      if (!account) account = accounts.find((a) => a.name === key);

      if (account) {
        if (
          account.type[0] !== "assets" &&
          account.type[0] !== "liabilities" &&
          account.type[0] !== "equity" &&
          account.type[0] !== "income"
        ) {
          map[account.id] = value;
        }
      }
    }

    return map;
  }
);

export const scheduleCNotMappedGeneralLedgerAccountsSelector = createSelector(
  [scheduleCMappingsSelector, scheduleCAccountsSelector],
  (scheduleCMappings, generalLedgerAccounts) => {
    const missing: typeof generalLedgerAccounts = [];
    for (const account of generalLedgerAccounts) {
      if (!scheduleCMappings[account.id]) missing.push(account);
    }
    return missing;
  }
);

export const scheduleCAreGroupingsMissingSelector = createSelector(
  [scheduleCMappingsSelector, scheduleCGroupingsSelector],
  (scheduleCMappings, scheduleCGroupings) => {
    let areGroupingsMissing = false;
    let otherExpenses = 0;
    for (const [account, expenseType] of Object.entries(scheduleCMappings)) {
      if (expenseType === "other-expenses") {
        otherExpenses++;
        if (!scheduleCGroupings[account]) {
          areGroupingsMissing = true;
          if (otherExpenses > 9) return true;
        }
      }
    }

    return areGroupingsMissing && otherExpenses > 9;
  }
);
